/**
 * Speech Recording Button Component
 * Material-UI button component for speech-to-text recording functionality
 * Integrates with useSpeechRecording hook and follows existing design patterns
 */

import React from 'react';
import { 
  IconButton, 
  Tooltip, 
  CircularProgress, 
  Box,
  Typography,
  Snackbar,
  Alert 
} from '@mui/material';
import { 
  Mic as MicIcon,
  Stop as StopIcon,
  MicOff as MicOffIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { useSpeechRecording } from '../../hooks/useSpeechRecording';
import { useUntypedTranslation } from '../../hooks/useUntypedTranslation';
import styles from './SpeechRecordingButton.module.scss';

/**
 * Props interface for SpeechRecordingButton
 */
interface SpeechRecordingButtonProps {
  /** Callback when transcribed text is available */
  onTranscribedText: (text: string) => void;
  /** Callback when recording state changes */
  onRecordingStateChange?: (isRecording: boolean) => void;
  /** Whether the button should be disabled */
  disabled?: boolean;
  /** Additional CSS class name */
  className?: string;
}

/**
 * Formats recording duration for display
 */
const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

/**
 * Speech Recording Button Component
 */
const SpeechRecordingButton: React.FC<SpeechRecordingButtonProps> = ({
  onTranscribedText,
  onRecordingStateChange,
  disabled = false,
  className = ''
}) => {
  const { t } = useUntypedTranslation();
  
  const {
    recordingState,
    isRecording,
    isPreparing,
    recordingDuration,
    transcribedText,
    error,
    startRecording,
    stopRecording,
    clearError,
    clearTranscribedText,
    isSupported
  } = useSpeechRecording();

  // Notify parent of recording state changes
  React.useEffect(() => {
    if (onRecordingStateChange) {
      onRecordingStateChange(isRecording);
    }
  }, [isRecording, onRecordingStateChange]);

  // Handle transcribed text callback
  const lastTranscribedTextRef = React.useRef<string | null>(null);
  const clearTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  
  React.useEffect(() => {
    if (transcribedText && transcribedText !== lastTranscribedTextRef.current) {
      // Store the current text to prevent duplicate processing
      lastTranscribedTextRef.current = transcribedText;
      
      // Deliver text to parent component
      onTranscribedText(transcribedText);
      
      // Clear transcribed text after delivery to allow user editing
      // Use setTimeout to ensure the delivery completes first
      clearTimeoutRef.current = setTimeout(() => {
        clearTranscribedText();
        lastTranscribedTextRef.current = null;
        clearTimeoutRef.current = null;
      }, 0);
    }
    
    // Cleanup timeout on unmount
    return () => {
      if (clearTimeoutRef.current) {
        clearTimeout(clearTimeoutRef.current);
        clearTimeoutRef.current = null;
      }
    };
  }, [transcribedText, onTranscribedText, clearTranscribedText]);

  /**
   * Handles button click based on current state
   */
  const handleClick = async () => {
    if (error) {
      clearError();
      return;
    }

    if (isRecording) {
      await stopRecording();
    } else if (recordingState === 'idle') {
      await startRecording();
    }
  };

  /**
   * Gets the appropriate icon based on current state
   */
  const getIcon = () => {
    switch (recordingState) {
      case 'preparing':
        return <CircularProgress size={24} color="inherit" />;
      case 'recording':
        return <StopIcon />;
      case 'uploading':
        return <CloudUploadIcon />;
      case 'processing':
        return <CircularProgress size={24} color="inherit" />;
      case 'done':
        return <CheckCircleIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'idle':
      default:
        return isSupported ? <MicIcon /> : <MicOffIcon />;
    }
  };

  /**
   * Gets the appropriate color based on current state
   */
  const getColor = (): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (recordingState) {
      case 'preparing':
        return 'primary';
      case 'recording':
        return 'error'; // Red for recording
      case 'uploading':
      case 'processing':
        return 'primary';
      case 'done':
        return 'success';
      case 'error':
        return 'error';
      case 'idle':
      default:
        return isSupported ? 'default' : 'error';
    }
  };

  /**
   * Gets the appropriate tooltip text based on current state
   */
  const getTooltipText = (): string => {
    if (!isSupported) {
      return t('speechRecording.notSupported');
    }

    if (error) {
      return error;
    }

    switch (recordingState) {
      case 'idle':
        return t('speechRecording.startRecording');
      case 'preparing':
        return t('speechRecording.preparing');
      case 'recording':
        return t('speechRecording.stopRecording');
      case 'uploading':
        return t('speechRecording.uploading');
      case 'processing':
        return t('speechRecording.processing');
      case 'done':
        return t('speechRecording.done');
      case 'error':
        return t('speechRecording.error');
      default:
        return '';
    }
  };

  /**
   * Determines if button should be disabled
   */
  const isDisabled = disabled || 
                    !isSupported || 
                    recordingState === 'preparing' ||
                    recordingState === 'uploading' || 
                    recordingState === 'processing' ||
                    recordingState === 'done';

  /**
   * Gets CSS classes for the button based on state
   */
  const getButtonClasses = (): string => {
    const classes = [styles.speechRecordingButton];
    
    if (className) {
      classes.push(className);
    }
    
    switch (recordingState) {
      case 'preparing':
        classes.push(styles['speechRecordingButton--preparing']);
        break;
      case 'recording':
        classes.push(styles['speechRecordingButton--recording']);
        break;
      case 'processing':
        classes.push(styles['speechRecordingButton--processing']);
        break;
      case 'error':
        classes.push(styles['speechRecordingButton--error']);
        break;
      case 'done':
        classes.push(styles['speechRecordingButton--success']);
        break;
    }
    
    return classes.join(' ');
  };

  return (
    <>
      <Box className={styles.speechRecordingContainer}>
        <Tooltip title={getTooltipText()} placement="top">
          <span> {/* Span wrapper needed for disabled button tooltips */}
            <IconButton
              onClick={handleClick}
              disabled={isDisabled}
              color={getColor()}
              className={getButtonClasses()}
              size="medium"
              aria-label={getTooltipText()}
            >
              {getIcon()}
            </IconButton>
          </span>
        </Tooltip>
        
        {/* Recording duration display */}
        {isRecording && (
          <Box className={styles.recordingDuration}>
            <Typography variant="caption" color="error">
              {formatDuration(recordingDuration)}
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Error message snackbar - follows app's error display pattern */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={clearError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={clearError} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SpeechRecordingButton;
